import { useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import { useCheckoutStateSelector } from "@checkout/context";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import type { Amount } from "@hopper-b2b/types";
import { BalanceToPayLineItem, PaymentMethod } from "@hsbc/portal";
import { RewardsAndCreditsLineItem } from "@hsbc/rewards";
import {
  amountToFiatPrice,
  noFractionOptions,
  twoFractionOptions,
} from "@hsbc/utilities";
import { Loading, type PriceBreakdownComponentProps } from "@tenant/ui";
import clsx from "clsx";
import { WrappedPriceDropDialog } from "../../../../overrides/flights/PricePredictionCard";
import { getProductTotalPrice } from "../../selectors";
import { FintechLineItem, PriceLineItem, SummaryLineItem } from "./components";
import styles from "./styles.module.scss";

const COMPONENT_KEY = CoreUiComponents.PriceBreakdown;

export type PriceBreakdownProps = PriceBreakdownComponentProps & {
  priceDropAmount?: Amount;
  subtitle?: string | JSX.Element;
};

export const PriceBreakdown = (props: PriceBreakdownProps) => {
  const {
    priceLineItems,
    summaryLineItems,
    fintechLineItems,
    className,
    currencyString,
    isLoading = false,
    children,
    title,
    subtitle,
    priceDropAmount,
  } = props;

  const { formatFiatCurrency } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { accountSpecific } = useCheckoutStateSelector(getProductTotalPrice);

  return (
    <div className={clsx(block, className)}>
      <div className={cn("header")}>
        <div className={cn("title")}>{title}</div>
        <div className={cn("subtitle")}>{subtitle}</div>
        {!!priceDropAmount && (
          <p className={cn("pdp-description")}>
            <Trans
              i18nKey="flightPriceBreakdown.priceDropProtectionDescription"
              components={[
                <WrappedPriceDropDialog
                  priceDropAmount={formatFiatCurrency(
                    amountToFiatPrice(priceDropAmount),
                    noFractionOptions
                  )}
                />,
              ]}
            />
          </p>
        )}
      </div>

      {priceLineItems.map((priceLine, index) => (
        <PriceLineItem key={index} {...priceLine} />
      ))}

      {!!fintechLineItems?.length && (
        <div className={cn("fintech")}>
          {fintechLineItems.map((fintechLineItemProps) => (
            <FintechLineItem
              key={fintechLineItemProps.title}
              {...fintechLineItemProps}
            />
          ))}
        </div>
      )}

      {summaryLineItems.map((summaryLine, index) => (
        <SummaryLineItem key={index} {...summaryLine} />
      ))}
      <RewardsAndCreditsLineItem
        accountSpecific={accountSpecific}
        fiatFormatOptions={twoFractionOptions}
      />

      <BalanceToPayLineItem fiatFormatOptions={twoFractionOptions} />

      <PaymentMethod product={Product.Flight} />

      {currencyString ? (
        <div className={cn("description")}>{currencyString}</div>
      ) : null}

      {isLoading ? (
        <div className={cn("loading")}>
          <Loading />
        </div>
      ) : null}

      {children}
    </div>
  );
};
